import { layoutAll, MATCHUP_W, MATCHUP_H, HEAD_H, SEAT_H, COL_GAP, PAD } from './layout.js'

export const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))

function statusText(matchup) {
  switch (matchup.status) {
    case 'ready': return matchup.schedule || 'Not played'
    case 'in_progress':
      // Every game played and still no winner: a tie, or a level series. A game
      // that is only scheduled leaves the matchup waiting to be played, not for
      // a result, so its date and venue still show.
      if (matchup.games.length >= matchup.best_of && matchup.games.every(g => g.recorded)) return 'Waiting for result'
      return matchup.schedule || `Game ${matchup.games.length + 1} of ${matchup.best_of}`
    case 'complete': return matchup.best_of > 1 ? `${matchup.top.wins}–${matchup.bottom.wins} series` : 'Final'
    case 'walkover': return 'Walkover'
    case 'skipped': return 'Not needed'
    case 'override': return (matchup.override && matchup.override.note) || 'Advanced'
    default: return matchup.play_if === 'both_alive' ? 'If needed' : ''
  }
}

function logoHtml(team) {
  if (team.logo) return `<img class="c-tournament-bracket_logo" src="${esc(team.logo)}" alt="">`
  const color = team.color || '#6B7683'
  return `<span class="c-tournament-bracket_logo c-tournament-bracket_logo-text" style="background:${esc(color)}">${esc(team.abbr)}</span>`
}

function seatHtml(matchup, side, opts) {
  const seat = matchup[side]
  const t = seat.team
  if (!t) {
    const editable = opts.editor && seat.seed != null
    return `<div class="c-tournament-bracket_seat is-empty${editable ? ' is-droppable' : ''}" data-js-seat="${esc(seat.address)}"><span class="c-tournament-bracket_seed">${seat.seed != null ? seat.seed : ''}</span><span class="c-tournament-bracket_name">${esc(seat.label)}</span></div>`
  }
  let result = ''
  if (matchup.games.length) {
    const val = matchup.best_of === 1 ? (seat.score != null ? seat.score : '–') : seat.wins
    result = `<span class="c-tournament-bracket_result">${esc(val)}</span>`
  }
  const champion = opts.championMatchup === matchup.key && opts.championId === t.id
  const cls = ['c-tournament-bracket_seat', seat.winner ? 'is-winner' : '', seat.loser ? 'is-loser' : '', champion ? 'is-champion' : '', opts.editor && seat.seed != null ? 'is-draggable is-droppable' : ''].filter(Boolean).join(' ')
  const drag = opts.editor && seat.seed != null ? ' draggable="true"' : ''
  const seed = t.seed != null ? t.seed : (seat.seed != null ? seat.seed : '')
  const name = `<span class="c-tournament-bracket_name">${esc(t.name)}</span>`
  const trophy = champion ? '<span class="c-tournament-bracket_trophy" role="img" aria-label="Champion" title="Champion">🏆</span>' : ''
  // Grip dots on a placed team in the editor, so it reads as draggable.
  const grip = drag ? '<span class="c-tournament-bracket_grip" aria-hidden="true" title="Drag to move"></span>' : ''
  return `<div class="${cls}" data-js-seat="${esc(seat.address)}"${drag}>${grip}<span class="c-tournament-bracket_seed">${esc(seed)}</span>${logoHtml(t)}${name}${trophy}${result}</div>`
}

// Accessible name for a matchup that opens its game or results.
function matchupLabel(matchup) {
  const teams = [matchup.top.team, matchup.bottom.team].map(t => t ? t.name : 'TBD').join(' vs ')
  return matchup.games.length === 1 ? `Open game: ${teams}` : `Show results: ${teams}`
}

export function matchupHtml(matchup, x, y, opts) {
  const meta = [matchup.best_of > 1 && matchup.status !== 'complete' ? `Best of ${matchup.best_of}` : '', statusText(matchup)].filter(Boolean).join(' · ')
  // A matchup with games opens them: the game page for one, the results popover
  // for a series. The placement editor drags seats instead.
  const opens = !opts.editor && matchup.games.length > 0
  const open = opens ? ` data-js-open-matchup="${esc(matchup.key)}" role="button" tabindex="0" aria-label="${esc(matchupLabel(matchup))}"` : ''
  return `<div class="c-tournament-bracket_matchup status-${matchup.status}${opens ? ' is-clickable' : ''}" data-js-matchup="${esc(matchup.key)}"${open} style="left:${x}px;top:${y}px">` +
    `<div class="c-tournament-bracket_matchup-head"><span class="c-tournament-bracket_matchup-key">${esc(matchup.name || matchup.key)}</span><span class="c-tournament-bracket_matchup-status">${esc(meta)}</span></div>` +
    seatHtml(matchup, 'top', opts) + seatHtml(matchup, 'bottom', opts) + '</div>'
}

// The matchup where the champion actually won it. Usually the final, but when
// the final is a reset game that wasn't needed ("skipped"), the title was
// decided in the game that fed it.
function championMatchupKey(payload, matchupsByKey) {
  if (!payload.champion) return null
  let key = payload.final
  const seen = new Set()
  while (key && matchupsByKey[key] && matchupsByKey[key].status === 'skipped' && !seen.has(key)) {
    seen.add(key)
    const feeders = matchupsByKey[key].feeders || {}
    key = feeders.top || feeders.bottom
  }
  return key
}

// Draws the whole tournament into `canvas`. Returns the layout so callers can
// size the viewport and pan to brackets.
export function renderCanvas(canvas, payload, opts = {}) {
  const all = layoutAll(payload)
  const abs = {}
  let html = ''
  let paths = ''
  const matchupsByKey = {}
  for (const b of payload.brackets) for (const r of b.rounds) for (const s of r.matchups) matchupsByKey[s.key] = s
  opts = { ...opts, championId: payload.champion && payload.champion.id, championMatchup: championMatchupKey(payload, matchupsByKey) }
  for (const blk of all.blocks) {
    const { layout, bracket } = blk
    const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`
    const count = bracket.entries ? plural(bracket.entries, 'team') : plural(Object.keys(layout.pos).length, 'game')
    if (blk.titled) {
      html += `<div class="c-tournament-bracket_title" data-js-bracket-title="${esc(bracket.key)}" style="left:${PAD}px;top:${blk.titleY}px">${esc(bracket.name)}<small>${count}</small></div>`
    }
    for (const [c, name] of Object.entries(layout.colLabels)) {
      html += `<div class="c-tournament-bracket_col-label" style="left:${PAD + c * (MATCHUP_W + COL_GAP)}px;top:${blk.labelsY}px">${esc(name)}</div>`
    }
    for (const r of bracket.rounds) for (const s of r.matchups) matchupsByKey[s.key] = s
    for (const [k, p] of Object.entries(layout.pos)) abs[k] = { x: PAD + p.x, y: blk.originY + p.y }
    for (const k of Object.keys(layout.pos)) html += matchupHtml(matchupsByKey[k], abs[k].x, abs[k].y, opts)
    for (const l of layout.links) {
      const fp = abs[l.from], sp = abs[l.to]
      const fy = fp.y + MATCHUP_H / 2
      const sy = sp.y + HEAD_H + (l.side === 'top' ? SEAT_H / 2 : SEAT_H * 1.5)
      let x1, x2
      if (fp.x < sp.x) { x1 = fp.x + MATCHUP_W; x2 = sp.x } else { x1 = fp.x; x2 = sp.x + MATCHUP_W }
      const mx = (x1 + x2) / 2
      const from = matchupsByKey[l.from]
      const decided = ['complete', 'walkover', 'skipped', 'override'].includes(from.status)
      paths += `<path class="c-tournament-bracket_link${decided ? ' is-decided' : ''}${l.kind === 'loser' ? ' is-loser' : ''}" d="M${x1},${fy} H${mx} V${sy} H${x2}"/>`
    }
  }
  canvas.style.width = all.width + 'px'
  canvas.style.height = all.height + 'px'
  canvas.innerHTML = `<svg class="c-tournament-bracket_links" width="${all.width}" height="${all.height}" viewBox="0 0 ${all.width} ${all.height}">${paths}</svg>` + html
  return { ...all, matchupsByKey, abs }
}
